'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["forwardedRef", "highlighted", "id", "itemComponent", "onMouseEnter", "option", "_useButton", "style"];
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { PureComponent, forwardRef } from 'react';
import UIButton from 'UIComponents/button/UIButton';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { OptionOrGroupType } from 'UIComponents/types/OptionTypes';
import { getComponentPropType } from 'UIComponents/utils/propTypes/componentProp';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBox from 'UIComponents/layout/UIBox';
import UIIcon from 'UIComponents/icon/UIIcon';

// No-break space ensures that blank items are sized properly
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BLANK_LABEL = '\u00A0';
class DrilldownResultsItemForwardRef extends PureComponent {
  getButtonClassName() {
    return 'drilldown-typeahead-result-label uiTypeaheadResults__item truncate-text';
  }
  getClassName({
    highlighted,
    option: {
      disabled,
      dropdownClassName
    }
  }) {
    return classNames(dropdownClassName, disabled ? 'drilldown-typeahead-result--disabled' : highlighted && 'drilldown-typeahead-result--highlighted');
  }
  getLabel({
    option: {
      buttonText,
      text,
      value
    }
  }) {
    const fallbackText = text || (value == null ? '' : value.toString());
    return buttonText == null ? fallbackText : buttonText;
  }
  getTitle({
    option: {
      text,
      title,
      value
    }
  }) {
    if (title === '') {
      return null;
    }
    return title || text || (value == null ? '' : value.toString());
  }
  renderLabel({
    computedLabel
  }) {
    if (!computedLabel) {
      return BLANK_LABEL;
    }
    return computedLabel;
  }
  renderContent({
    renderedLabel
  }) {
    return /*#__PURE__*/_jsx("span", {
      className: "drilldown__item__label",
      "data-option-text": true,
      children: renderedLabel
    });
  }
  renderButton({
    rest
  }, {
    computedButtonClassName,
    computedTitle,
    renderedContent
  }) {
    const {
      option
    } = this.props;
    if (option.children) {
      return /*#__PURE__*/_jsx(UITooltip, {
        placement: "left",
        title: option.disabledTooltip,
        disabled: !option.disabled,
        children: /*#__PURE__*/_jsx(UIButton, Object.assign({}, rest, {
          className: computedButtonClassName,
          tabIndex: -1,
          title: computedTitle,
          use: "unstyled",
          children: /*#__PURE__*/_jsxs(UIFlex, {
            align: "center",
            children: [/*#__PURE__*/_jsx(UIBox, {
              grow: 1,
              children: renderedContent
            }), /*#__PURE__*/_jsx(UIBox, {
              children: /*#__PURE__*/_jsx(UIIcon, {
                name: "right"
              })
            })]
          })
        }))
      });
    }
    return /*#__PURE__*/_jsx(UITooltip, {
      placement: "left",
      title: option.disabledTooltip,
      disabled: !option.disabled,
      children: /*#__PURE__*/_jsx(UIButton, Object.assign({}, rest, {
        className: computedButtonClassName,
        tabIndex: -1,
        title: computedTitle,
        use: "unstyled",
        children: renderedContent
      }))
    });
  }
  render() {
    const _this$props = this.props,
      {
        forwardedRef,
        highlighted,
        id,
        itemComponent: Item,
        onMouseEnter,
        option,
        _useButton,
        style
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const computedAriaSelected = highlighted || null;
    const computedItemClassName = this.getClassName({
      highlighted,
      option
    });
    const computedButtonClassName = this.getButtonClassName();
    const computedTitle = this.getTitle({
      option
    });
    const computedLabel = this.getLabel({
      option
    });
    const renderedLabel = this.renderLabel({
      computedLabel
    });
    let renderedContent = this.renderContent({
      renderedLabel
    });

    // If _useButton={true}, wrap content in a button
    renderedContent = _useButton ? this.renderButton({
      rest
    }, {
      computedButtonClassName,
      computedTitle,
      renderedContent
    }) : renderedContent;
    if (typeof Item === 'function') {
      return /*#__PURE__*/_jsx(Item, Object.assign({}, rest, {
        "aria-selected": computedAriaSelected,
        id: id,
        onMouseEnter: onMouseEnter,
        option: option,
        className: computedItemClassName,
        "data-option-value": option.value,
        role: "option",
        children: renderedContent
      }));
    }
    return /*#__PURE__*/_jsx(Item, {
      "aria-selected": computedAriaSelected,
      className: computedItemClassName,
      "data-test-id": `test-drilldown-option-${option.value}`,
      "data-option-value": option.value,
      id: id,
      onMouseEnter: onMouseEnter,
      role: "option",
      ref: forwardedRef,
      style: style,
      children: renderedContent
    });
  }
}
const DrilldownResultsItem = /*#__PURE__*/forwardRef((props, ref) => {
  return /*#__PURE__*/_jsx(DrilldownResultsItemForwardRef, Object.assign({}, props, {
    forwardedRef: ref
  }));
});
DrilldownResultsItemForwardRef.displayName = 'DrilldownResultsItem';
DrilldownResultsItem.propTypes = {
  highlighted: PropTypes.bool.isRequired,
  itemComponent: getComponentPropType({
    propTypes: {
      'aria-selected': PropTypes.bool,
      'data-option-value': PropTypes.string,
      id: PropTypes.string,
      className: PropTypes.string,
      option: OptionOrGroupType,
      onMouseEnter: PropTypes.func,
      children: PropTypes.node
    }
  }),
  option: OptionOrGroupType.isRequired,
  _useButton: PropTypes.bool
};
DrilldownResultsItem.defaultProps = {
  highlighted: false,
  itemComponent: 'li',
  _useButton: true
};
export default DrilldownResultsItem;